import { useApolloClient } from '@apollo/client';
import gql from 'graphql-tag';
import React from 'react';
import { getPrimary, getSecondary } from '../../styles/colors';
import {
	AlertConfirm,
	BasicButton,
	TextInput
} from '../../UI';
import { moment } from '../../containers/App';
import FormRootLayout from './FormRootLayout';
import { getText } from '../../template/utils/functions';
import { NO_FORM_SECTIONS } from '../../utils/constants';
import BlockTitle from '../../UI/BlockTitle';
import { MainAppContext } from '../../containers/MainAppProvider';
import CopyPortalModal from './CopyPortalModal';

const PortalStates = ({
	portal,
	updateAndSave,
	translate,
	refetch,
	state
}) => {
	const [modalTitle, setModalTitle] = React.useState(false);
	const [modalRestart, setModalRestart] = React.useState(false);
	const [modalUpdate, setModalUpdate] = React.useState(false);
	const [modalCopy, setModalCopy] = React.useState(false);
	const [status, setStatus] = React.useState();
	const [error, setError] = React.useState();
	const [devConfig, setDevConfig] = React.useState(portal.config);
	const [namePortal, setNamePortal] = React.useState(portal.name);
	const client = useApolloClient();
	const mainAppContext = React.useContext(MainAppContext);
	const newDate = new Date();
	const dateString = newDate.toISOString();
	const datePortal = new Date().toISOString();

	const activatePortal = async value => {
		await client.mutate({
			mutation: gql`
                mutation activeCouncilPortal($portal: CouncilPortalInput!, $date: String!){
                    activeCouncilPortal(portal: $portal, date: $date){
                        success
                        message
                    }
                }
            `,
			variables: {
				portal: {
					id: portal.id,
					state: value
				},
				date: datePortal
			}
		});
		refetch();
	};

	const updateName = async () => {
		if (namePortal === portal.name) {
			setError('Este nombre ya está en uso'); // TRADUCCIÓN
			return;
		}
		const checkName = await client.query({
			query: gql`
                query CouncilPortal($name: String!){
                    councilPortal(name: $name){
                        name
                    }
                }
            `,
			variables: {
				name: namePortal
			}
		});
		if (checkName?.data?.councilPortal) {
			setError('Este nombre ya está en uso'); // TRADUCCIÓN
			return;
		}
		updateAndSave({
			name: namePortal
		});
		setModalTitle(false);
	};

	const resetPortal = async () => {
		const result = await client.mutate({
			mutation: gql`
		        mutation ResetCouncilPortal($id: Int!, $date: String!){
		            resetCouncilPortal(id: $id, date: $date){
                        success
                        message
		            }
		        }
		 `,
			variables: {
				id: portal.id,
				date: portal.council.dateStart,
			}
		});
		if (result?.errors?.length > 0) {
			setError(result.errors[0].message);
			return;
		}
		refetch();
		setModalRestart(false);
	};


	const updatePortal = async value => {
		const result = await client.mutate({

			mutation: gql`
                mutation UpdatePortal($portal: CouncilPortalInput!){
                    updateCouncilPortal(portal: $portal){
                        success
                        message
                    }
                }
            `,
			variables: {
				portal: {
					id: portal.id,
					...value
				}
			}
		});
		if (result?.errors?.length > 0) {
			setError(result?.errors[0].message);
			return;
		}
		refetch();
		setModalUpdate(false);
	};

	React.useEffect(() => {
		if (portal.state === 0) {
			setStatus('Portal desactivado');
			return;
		}
		if (dateString < portal?.config?.portalDates?.open?.general?.date) {
			setStatus('Este portal aún no se ha abierto automaticamente debido a la configuración de la fecha de apertura');
			return;
		}
		if (dateString > portal?.config?.portalDates?.close?.general?.date) {
			setStatus('Este portal esta cerrado automaticamente por la configuración de la fecha de cierre');
			return;
		}
		setStatus(null);
	}, [portal?.state, portal?.config?.portalDates]);

	React.useEffect(() => {
		setError();
	}, [modalCopy, modalRestart, modalUpdate]);

	const renderForm = () => (
		<section style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
			{/* INITIAL SET UP */}
			<section style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
				{/* PORTAL URL */}
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<div style={{ display: 'flex', gap: '0.5em', alignItems: 'start' }}>
						<BlockTitle text={'URL del Portal:'} />
						<a
							rel="noreferrer"
							href={`${window.location.origin}/${state.name}`}
							target="_blank"
						>
							{window.location.origin}/{state.name}
						</a>
					</div>
					<div style={{ display: 'flex', gap: '0.5em', alignItems: 'start' }}>
						<BlockTitle text={'Estado:'} />
						<label
							style={{
								backgroundColor: status ? 'red' : 'green',
								color: 'white',
								padding: '2px'
							}}
						>
							{status || 'Portal publicado'}
						</label>
					</div>
				</div>
				{/* BUTTONS */}
				<div className='ml-2 flex flex-auto flex-col flex-wrap gap-2 justify-around items-start content-start sm:flex-row'>
					{/* EDIT NAME */}
					<div
						className='flex-1'
						style={{ flexBasis: 'auto', maxWidth: '9.55em' }}
					>
						<BasicButton
							backgroundColor={{
								width: '100%',
								backgroundColor: 'white',
								border: `1px solid ${getSecondary()}`,
								boxShadow: 'none',
								color: getSecondary(),
								fontWeight: portal.state === 1 ? 'bold' : 'normal',
							}}
							color="white"
							text={'Editar nombre'}
							onClick={() => setModalTitle(true)}
						/>
					</div>
					{/* ACTIVATE PORTAL */}
					<div
						className='flex-1'
						style={{ flexBasis: 'auto', maxWidth: '9.55em' }}
					>
						<BasicButton
							text={portal.state === 1 ? 'Desactivar portal' : 'Activar portal'}
							onClick={() => activatePortal(portal.state === 1 ? 0 : 1)}
							backgroundColor={{
								width: '100%',
								backgroundColor: 'white',
								border: portal.state === 1 ? '1px solid #a94c4c' : `1px solid ${getPrimary()}`,
								boxShadow: 'none',
								color: portal.state === 1 ? '#a94c4c' : getPrimary(),
								fontWeight: portal.state === 1 ? 'bold' : 'normal',
							}}
						/>
					</div>
					{/* COPY PORTAL */}
					<div
						className='flex-1'
						style={{ flexBasis: 'auto', maxWidth: '9.55em' }}
					>
						<BasicButton
							text={'Copiar portal'}
							onClick={() => setModalCopy(true)}
							backgroundColor={{
								width: '100%',
								backgroundColor: 'white',
								border: `1px solid ${getPrimary()}`,
								boxShadow: 'none',
								color: getPrimary(),
								fontWeight: portal.state === 1 ? 'bold' : 'normal',
							}}
						/>
					</div>
					{/* UPDATE PORTAL */}
					<div
						className='flex-1'
						style={{ flexBasis: 'auto', maxWidth: '9.55em' }}
					>
						<BasicButton
							text={'Actualizar Portal'}
							onClick={() => setModalUpdate(true)}
							backgroundColor={{
								width: '100%',
								backgroundColor: getSecondary(),
								border: `1px solid ${getSecondary()}`,
								boxShadow: 'none',
								color: 'white',
								fontWeight: portal.state === 1 ? 'bold' : 'normal',
							}}
						/>
					</div>
					{/* RESTART PORTAL */}
					<div
						className='flex-1'
						style={{ flexBasis: 'auto', maxWidth: '9.55em' }}
					>
						<BasicButton
							text={'Reiniciar Portal'}
							onClick={() => setModalRestart(true)}
							backgroundColor={{
								width: '100%',
								backgroundColor: getPrimary(),
								border: `1px solid ${getPrimary()}`,
								boxShadow: 'none',
								color: 'white',
								fontWeight: portal.state === 1 ? 'bold' : 'normal',
							}}
						/>
					</div>
				</div>
			</section>

			{/* PORTAL SUMMARY */}
			<section style={{ display: 'flex', flexDirection: 'column', gap: '1em' }}>
				<BlockTitle text={'Resumen del portal'} style={{ fontSize: '1.2rem' }} />
				{/* PORTAL SECTIONS */}
				<div>
					<BlockTitle text={'Secciones en portal'} />
					<div style={{ display: 'flex', gap: '2rem' }}>
						{Object.values(portal?.config?.sections)
							.filter(section => !NO_FORM_SECTIONS.includes(section.value))
							.map(section => (
								section?.isActive
								&& <span key={section?.value}>{getText(section, translate)}</span>
							))}
					</div>
				</div>
				{/* OPEN DATES */}
				<div>
					<BlockTitle text={'Fecha apertura portal'} />
					<div style={{
						display: 'flex', flexDirection: 'column', gap: '1em'
					}}>
						<div style={{ display: 'flex' }}>
							<span style={{ minWidth: '200px' }}>
								General
							</span>
							<span>
								{moment(portal?.config?.portalDates?.open?.general?.date).format('DD/MM/YYYY HH:mm:ss')}
							</span>
						</div>
						{portal?.config?.portalDates?.open?.option === 2
							&& Object.values(portal.config.sections)
								.filter(section => !NO_FORM_SECTIONS.includes(section.value) && section.isActive)
								.map(section => (
									<div
										key={section.value}
										style={{ display: 'flex' }}
									>
										<div style={{ minWidth: '200px' }}>{getText(section, translate)}</div>
										<div>
											{moment(
												(portal.config.portalDates?.open?.[section.value]) ?
													portal.config.portalDates?.open?.[section.value]?.date
													: portal.council.dateStart
											).format('DD/MM/YYYY HH:mm:ss')}
										</div>
									</div>
								))
						}
					</div>
				</div>
				{/* CLOSE DATES */}
				<div>
					<BlockTitle text={'Fecha cierre portal'} />
					<div style={{
						display: 'flex', flexDirection: 'column', gap: '1em'
					}}>
						<div style={{ display: 'flex' }}>
							<span style={{ minWidth: '200px' }}>
								General
							</span>
							<span>
								{moment(portal?.config?.portalDates?.close?.general?.date).format('DD/MM/YYYY HH:mm:ss')}
							</span>
						</div>
						{portal?.config?.portalDates?.close?.option === 2
							&& Object.values(portal.config.sections)
								.filter(section => !NO_FORM_SECTIONS.includes(section.value) && section.isActive)
								.map(section => (
									<div
										key={section.value}
										style={{ display: 'flex' }}
									>
										<div style={{ minWidth: '200px' }}>{getText(section, translate)}</div>
										<div>
											{moment(
												(portal.config.portalDates.close?.[section.value]) ?
													portal.config.portalDates.close?.[section.value]?.date
													: portal.council.dateStart
											).format('DD/MM/YYYY HH:mm:ss')}
										</div>
									</div>
								))
						}
					</div>
				</div>

				{mainAppContext.state.user.roles === 'devAdmin'
					&& <div style={{
						display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem'
					}}>
						<BlockTitle text='Editar configuración directamente (VISTA DEV)' />
						<input
							type='textarea'
							value={JSON.stringify(devConfig)}
							onChange={event => {
								setDevConfig(JSON.parse(event.target.value));
							}}
							style={{
								padding: '2px',
								border: `2px solid ${getPrimary()}`
							}}
						/>
						<BasicButton
							color='white'
							text={translate.save}
							onClick={() => {
								updateAndSave({
									config: devConfig
								});
							}}
							backgroundColor={{
								backgroundColor: getSecondary(),
								border: `1px solid ${getSecondary()}`,
								boxShadow: 'none',
								color: 'white',
								fontWeight: 'bold'
							}}
						/>
						<BlockTitle text='Portal config' style={{ marginTop: '1rem' }} />
						<div><pre>{JSON.stringify(devConfig, null, 2)}</pre></div>
					</div>}

				{/* POP UPS */}
				<AlertConfirm
					requestClose={() => setModalTitle(false)}
					open={modalTitle}
					acceptAction={() => updateName()}
					buttonAccept={translate.accept}
					buttonCancel={translate.cancel}
					bodyText={
						<div>
							<TextInput
								floatingText={translate.name}
								onChange={event => setNamePortal(event.target.value.trim().replace(/[^a-z0-9]/gi, '').toLowerCase())}
								value={namePortal}
								errorText={error}
							/>
						</div>
					}
					title={translate.edit}
				/>
				{/* RESET PORTAL */}
				<AlertConfirm
					requestClose={() => setModalRestart(false)}
					open={modalRestart}
					acceptAction={() => resetPortal()}
					buttonAccept={translate.accept}
					buttonCancel={translate.cancel}
					bodyText={
						<div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
							<span style={{ color: 'red' }}>
								CUIDADO: Esta operación sobreescribirá cualquier personalización que exista actualmente. La operación no se podrá deshacer.
							</span>
							<span>
								Esta operación borrará toda la personalización del portal y lo revertirá a la configuración inicial.
							</span>
							<span style={{ color: 'red' }}>{error}</span>
						</div>
					}
					title={translate.confirm}
				/>
				{/* UPDATE PORTAL */}
				<AlertConfirm
					requestClose={() => setModalUpdate(false)}
					open={modalUpdate}
					acceptAction={() => updatePortal()}
					buttonAccept={translate.accept}
					buttonCancel={translate.cancel}
					bodyText={
						<div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
							<span>
								Esta operación actualizará la estructura interna del portal manteniendo la personalización.
							</span>
							<span style={{ color: 'red' }}>{error}</span>
						</div>
					}
					title={translate.confirm}
				/>
				{/* COPY PORTAL */}
				<CopyPortalModal
					open={modalCopy}
					portal={portal}
					refetch={refetch}
					requestClose={() => setModalCopy(false)}
					translate={translate}
					setModalCopy={setModalCopy}
				/>
			</section>
		</section>
	);

	return (
		<FormRootLayout
			stylesHeight={{ height: '100%' }}
			form={renderForm()}
		/>
	);
};

export default PortalStates;
